import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Col,
  Loader,
  Row, Text,
  usePageNumberFromUrl,
  usePerPageValueFromUrl,
  useSortValueFromUrl,
} from 'idsk-react-components';
import { useSearchParams } from 'react-router-dom';
import {
  MobileFilterModal,
  Pagination,
  useResponsiveContext,
} from 'slovlex-idsk-react-components';
import PredbeznaInformaciaListComponent from './component/PredbeznaInformaciaListComponent';
import useLegislativnyMaterialFilterList
  from '../LegislativnyMaterialList/hooks/useLegislativnyMaterialFilterList';
import LegMaterialListFilter from '../../../model/legislativnyMaterial/LegMaterialListFilter';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import getPaginationTotalLabel from '../../../function/getPaginationTotalLabel';
import countActiveFilters from '../../../function/countActiveFilters';
import { defaultPerPageValue, perPageDataArray } from '../../../constants/PaginationPerPageConstants';
import {
  defaultSortPredbezne,
} from '../../../constants/SortConstants';
import getUpdatedSearchParams from '../LegislativnyMaterialList/function/getUpdatedSearchParams';
import FilterTagsComponent from '../LegislativnyMaterialList/component/FilterTagsComponent';
import SidePanel from '../LegislativnyMaterialList/component/SidePanel';
import searchPicture from '../../../../assets/images/search-icon.svg';
import { StadiumMaterialuEnum } from '../../../model/legislativnyMaterial/StadiumMaterialu';
import emptyFilter from '../LegislativnyMaterialList/emptyFilter';
import PredbeznaInformaciaListSort from './component/PredbeznaInformaciaListSort';
import SearchInput from '../LegislativnyMaterialList/component/SearchInput';
import MobileFilters from '../LegislativnyMaterialList/component/MobileFilters';

interface Props {
  initialFilter: LegMaterialListFilter;
}

// datum zmeny od/do je pre pouzivatela jedno kriterium - jeden panel v bocnom
// filtri a jeden filter tag
const criteriaGroups: Array<Array<keyof LegMaterialListFilter>> = [
  ['datumZmenyOd', 'datumZmenyDo'],
];

const PredbeznaInformaciaList = ({ initialFilter }: Props): ReactElement => {
  const [firstLoad, setFirstLoad] = useState(true);
  const { page } = usePageNumberFromUrl('page');
  const { perPageValue } = usePerPageValueFromUrl('perPage', perPageDataArray, defaultPerPageValue);
  const [searchParams, setSearchParams] = useSearchParams();
  const { sort, setSort } = useSortValueFromUrl();
  const [filter, setFilter] = useState<LegMaterialListFilter>(initialFilter);
  const [filterCopy, setFilterCopy] = useState<LegMaterialListFilter>(filter);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const {
    legislativnyMaterialPage,
    requestStatus,
  } = useLegislativnyMaterialFilterList(filter, page, true);
  const { isSmallScreen } = useResponsiveContext();

  const onUpdateField: SafeParameterSetter<LegMaterialListFilter> = (fieldName, value): void => {
    setFilter({
      ...filter,
      ...filterCopy,
      [fieldName]: value,
    });
    setFilterCopy({
      ...filter,
      ...filterCopy,
      [fieldName]: value,
    });
  };

  const onUpdateFieldCopy: SafeParameterSetter<LegMaterialListFilter> = (fieldName, value): void => {
    setFilterCopy(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const onRecordsPerPageChange = (value: number): void => {
    const updatedSearchParams = new URLSearchParams(searchParams.toString());
    updatedSearchParams.set('perPage', value.toString());
    setSearchParams(updatedSearchParams.toString());
  };

  useEffect(() => {
    if (Array.from(searchParams.values()).length === 0
      && JSON.stringify(filter) !== JSON.stringify(emptyFilter)) {
      setFilter(emptyFilter);
      setFilterCopy(emptyFilter);
      setFirstLoad(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!firstLoad) {
      const urlParamsFromFilter = getUpdatedSearchParams(1, perPageValue, sort, filter);
      setSearchParams(urlParamsFromFilter);
    }
    setFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);
  const reloadFilterAndSort = (): void => {
    setSort([defaultSortPredbezne]);
    setFilter({ ...emptyFilter, stadium: StadiumMaterialuEnum.VYJADRENIA });
    setFilterCopy({ ...emptyFilter, stadium: StadiumMaterialuEnum.VYJADRENIA });
  };

  const handleCloseModal = ():void => {
    setIsOpenModal(false);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-close',
    }, '*');
    setFilter(filterCopy);
  };

  const handleOpenModal = ():void => {
    setIsOpenModal(true);
    window.parent.postMessage({
      type: 'iframe-to-parent-filter-modal-open',
    }, '*');
    setFilter(filterCopy);
  };

  const clearFilters = (): void => {
    setFilter(emptyFilter);
    setFilterCopy(emptyFilter);
  };

  // tlacidlo na vymazanie filtra sa podla zadania zobrazuje az pri dvoch a viac kriteriach
  const onClearFilters = countActiveFilters(filter, criteriaGroups) >= 2 ? clearFilters : null;

  return (
    <div className="legislativny-material-list-component">
      <Text className="elegislativa-rychle-filtrovanie">
        <img src={searchPicture} alt="search" />
        Rýchle filtrovanie:&nbsp;&nbsp;
        <Button
          buttonStyle="link"
          size="small"
          onClick={() => reloadFilterAndSort()}
          aria-label="Rýchle filtrovanie, Aktuálne predbežné informácie"
        >Aktuálne predbežné informácie
        </Button>
      </Text>
      {isSmallScreen && (
        <SearchInput
          filterCopy={filterCopy}
          label="Hľadaný výraz"
          filterSearchText={filterCopy?.searchTextVyraz}
          fieldName="searchTextVyraz"
          onUpdateFieldCopy={onUpdateFieldCopy}
          setFilter={setFilter}
          className="slovlex-idsk-side-panel-input-wrapper"
        />
      )}
      <MobileFilterModal
        isOpen={isOpenModal}
        handleClose={handleCloseModal}
        handleOpen={handleOpenModal}
        numberOfResults={legislativnyMaterialPage?.totalElements || 0}
        filters={(
          <MobileFilters
            isPredbezna
            filter={filter}
            setFilter={setFilter}
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            onUpdateField={onUpdateField}
          />
        )}
        perPageDataArray={perPageDataArray}
        onPerPageChange={onRecordsPerPageChange}
        perPageValue={perPageValue}
        clearFilters={onClearFilters}
        filterTags={(
          <FilterTagsComponent
            filter={filter}
            onUpdateField={onUpdateField}
          />
        )}
      />
      {!isOpenModal && (
      <Row>
        {!isSmallScreen && (
        <Col className="side-panel-column" width="one-quarter">
          <SidePanel
            setFilter={setFilter}
            isPredbezna
            filterCopy={filterCopy}
            onUpdateFieldCopy={onUpdateFieldCopy}
            filter={filter}
            onUpdateField={onUpdateField}
          />
        </Col>
        )}
        <Col width="three-quarters">
          <FilterTagsComponent
            isPredbezna
            filter={filter}
            onUpdateField={onUpdateField}
            onClearFilters={onClearFilters}
          />
          <Loader loading={requestStatus.loading}>
            <>
              {isSmallScreen && (
              <Row className="elegislativa-mobile-sort-select-row">
                <Col>
                  <PredbeznaInformaciaListSort />
                </Col>
              </Row>
              )}
              <Row className="pagination-label-sort-row">
                <Col widthDesktop="one-third" width="full" className={isSmallScreen && 'pagination-label-mobile'}>
                  <Text className="pagination-label">{getPaginationTotalLabel(legislativnyMaterialPage, page)}</Text>
                </Col>
                {!isSmallScreen && (
                <Col width="full" widthDesktop="two-thirds">
                  <PredbeznaInformaciaListSort />
                </Col>
                )}
              </Row>
              {legislativnyMaterialPage && (
              <div className="legislativny-material-list">
                <PredbeznaInformaciaListComponent
                  isSelectedFilter={JSON.stringify(filter) !== JSON.stringify(emptyFilter)}
                  legislativnyMaterialPage={legislativnyMaterialPage}
                />
                <Row className="legislativny-material-list-pagination-row">
                  <Col>
                    <Pagination
                      recordsPerPage={perPageDataArray}
                      hideTotal
                      number={page}
                      size={legislativnyMaterialPage.size}
                      totalElements={legislativnyMaterialPage.totalElements}
                      perPageValue={perPageValue}
                      onRecordsPerPageChange={onRecordsPerPageChange}
                    />
                  </Col>
                </Row>
              </div>
              )}
            </>
          </Loader>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default PredbeznaInformaciaList;
